import type { Currency, Unit } from 'chery-contracts';
import { BINARY_OPS, OP_GLYPHS, tokenLabel, type FormulaToken } from 'chery-kpi-engine';
import { Delete } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { cn } from '../../lib/cn';
import { PILLAR_META } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { formulaStatus, operandsByPillar, parseConstant, type FormulaOperand } from './formula-display';

export interface FormulaBuilderProps {
  tokens: readonly FormulaToken[];
  onChange: (tokens: FormulaToken[]) => void;
  /** KPIs the formula may use — already limited to visible, non-Text KPIs other than this one. */
  operands: readonly FormulaOperand[];
  /** The KPI's declared unit and currency, which the formula's result is checked against. */
  unit: Unit;
  currency: Currency | null;
  disabled?: boolean;
}

const isOperator = (token: FormulaToken): boolean => token.kind !== 'kpi' && token.kind !== 'const';

/**
 * The visual formula builder (plan §3.1, §7): KPI and constant chips joined by operator and bracket chips. It only
 * ever produces a token sequence, which the engine's shunting-yard turns into the JSON AST that is saved — there is
 * no formula text to type and nothing is evaluated as code. Under the chips: the live preview with KPI names, the
 * structural error, what the formula produces against the declared unit, and the warnings Save will ask about.
 */
export const FormulaBuilder = ({ tokens, onChange, operands, unit, currency, disabled = false }: FormulaBuilderProps) => {
  const { t } = useI18nTranslation();
  const [constant, setConstant] = useState('');
  const nameOf = useMemo(() => {
    const names = new Map(operands.map((operand) => [operand.id, operand.name]));

    return (id: string): string => names.get(id) ?? t('kpis.formula.unknownKpi');
  }, [operands, t]);
  const status = useMemo(() => formulaStatus(tokens, { unit, currency }, operands), [tokens, unit, currency, operands]);
  const groups = useMemo(() => operandsByPillar(operands), [operands]);
  const constantValue = parseConstant(constant);

  const push = (token: FormulaToken): void => {
    if (!disabled) {
      onChange([...tokens, token]);
    }
  };

  const addConstant = (): void => {
    if (constantValue !== null) {
      push({ kind: 'const', value: constantValue });
      setConstant('');
    }
  };

  return (
    <div
      className="space-y-2"
      data-formula-builder
    >
      <div
        data-formula-tokens
        aria-label={t('kpis.formula.tokens')}
        className={cn(
          'flex min-h-[2.5rem] flex-wrap items-center gap-1.5 rounded-md border border-border p-2',
          disabled ? 'bg-muted/50' : 'bg-background',
        )}
      >
        {tokens.length === 0 ? <span className="text-xs text-muted-foreground">{t('kpis.formula.empty')}</span> : null}
        {tokens.map((token, index) => (
          <span
            // Tokens have no identity of their own; their position is what the builder edits.
            key={index}
            className={cn(
              'rounded px-1.5 py-0.5 font-mono text-xs font-semibold',
              isOperator(token) ? 'bg-muted text-foreground' : 'bg-violet-100 text-violet-900 dark:bg-violet-950/50 dark:text-violet-100',
            )}
          >
            {tokenLabel(token, nameOf)}
          </span>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <select
          aria-label={t('kpis.formula.addKpi')}
          data-formula-kpi
          value=""
          disabled={disabled}
          onChange={(event) => {
            if (event.target.value) {
              push({ kind: 'kpi', kpiId: event.target.value });
            }
          }}
          className="h-8 max-w-[15rem] rounded-md border border-border bg-background px-2 text-xs"
        >
          <option value="">{t('kpis.formula.addKpi')}</option>
          {groups.map((group) => (
            <optgroup
              key={group.pillar}
              label={PILLAR_META[group.pillar].shortLabel}
            >
              {group.operands.map((operand) => (
                <option
                  key={operand.id}
                  value={operand.id}
                >
                  {operand.name} ({operand.code}){operand.status === 'INACTIVE' ? ` · ${t('enums.status.INACTIVE')}` : ''}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        <Input
          aria-label={t('kpis.formula.constant')}
          data-formula-constant
          value={constant}
          disabled={disabled}
          inputMode="decimal"
          placeholder="100"
          onChange={(event) => setConstant(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addConstant();
            }
          }}
          className="h-8 w-20 text-xs"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={disabled || constantValue === null}
          onClick={addConstant}
        >
          {t('kpis.formula.addConstant')}
        </Button>

        {BINARY_OPS.map((op) => (
          <Button
            key={op}
            size="sm"
            variant="outline"
            aria-label={t(`kpis.formula.op.${op === '+' ? 'add' : op === '-' ? 'subtract' : op === '*' ? 'multiply' : 'divide'}`)}
            data-formula-op={op}
            disabled={disabled}
            onClick={() => push({ kind: 'op', op })}
            className="w-8 px-0 font-mono"
          >
            {OP_GLYPHS[op]}
          </Button>
        ))}
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => push({ kind: 'lparen' })}
          className="w-8 px-0 font-mono"
        >
          (
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => push({ kind: 'rparen' })}
          className="w-8 px-0 font-mono"
        >
          )
        </Button>

        <span className="ms-auto inline-flex gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('kpis.formula.backspace')}
            disabled={disabled || tokens.length === 0}
            onClick={() => onChange(tokens.slice(0, -1))}
          >
            <Delete className="size-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={disabled || tokens.length === 0}
            onClick={() => onChange([])}
          >
            {t('kpis.formula.clear')}
          </Button>
        </span>
      </div>

      <div
        data-formula-status
        className="min-h-[1.25rem] space-y-1 text-xs"
      >
        {status.kind === 'invalid' ? <p className="text-destructive">{status.message}</p> : null}
        {status.kind === 'ok' ? (
          <>
            <p className="text-muted-foreground">
              {t('kpis.formula.preview')}{' '}
              <span
                data-formula-preview
                className="font-mono text-foreground"
              >
                {status.preview}
              </span>
            </p>
            {status.unitError ? (
              <p className="text-destructive">{status.unitError}</p>
            ) : (
              <p className="text-muted-foreground">{t('kpis.formula.produces', { unit: status.produces })}</p>
            )}
            {status.warnings.map((warning) => (
              <p
                key={warning}
                className="text-amber-700 dark:text-amber-300"
              >
                {warning}
              </p>
            ))}
          </>
        ) : null}
      </div>
    </div>
  );
};
